import React, { useState } from "react";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { CardGiftcard } from "@mui/icons-material";
import { Control, FieldArrayWithId, FieldErrors } from "react-hook-form";
import { Product } from "../../../types";
import { ProductSearchAutocomplete } from "../../common/ProductSearchAutocomplete";
import { OrderLineRow } from "./OrderLineRow";
import { OrderFormData, OrderFormLine } from "./orderFormSchema";

interface Props {
    fields: FieldArrayWithId<OrderFormData, "items", "id">[];
    lines: OrderFormLine[];
    productDetailsById: Map<string, Partial<Product>>;
    control: Control<OrderFormData>;
    errors: FieldErrors<OrderFormData>;
    onAddProduct: (product: Product, asGift: boolean) => string;
    onQuantity: (index: number, quantity: number) => void;
    onUnitPrice: (index: number, price: number) => void;
    onRemove: (index: number) => void;
    /** Edit mode keeps at least one line (cancel the order instead). */
    keepOneLine: boolean;
    /** Units each saved line already holds, by item id (edit mode). */
    reservedByItemId: Map<string, number>;
    checkStock: boolean;
    itemHints?: Record<number, string>;
}

/**
 * The order's products: one search box adds a line (or one more of a line
 * already there); lines show photo, stock, quantity, price and total.
 */
export const ItemsSection: React.FC<Props> = ({
    fields,
    lines,
    productDetailsById,
    control,
    errors,
    onAddProduct,
    onQuantity,
    onUnitPrice,
    onRemove,
    keepOneLine,
    reservedByItemId,
    checkStock,
    itemHints,
}) => {
    const [addAs, setAddAs] = useState<"product" | "gift">("product");
    const [pickerKey, setPickerKey] = useState(0);
    const [hint, setHint] = useState("");

    const add = (product: Product | null) => {
        if (!product) return;
        setHint(onAddProduct(product, addAs === "gift"));
        setPickerKey((k) => k + 1); // clear the search box
        setAddAs("product"); // back to paid, so nothing is added free by accident
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                    <ProductSearchAutocomplete
                        key={pickerKey}
                        value={null}
                        onChange={add}
                        label={addAs === "gift" ? "Add a free gift — name, SKU or scan" : "Add a product — name, SKU or scan"}
                        secondaryLine="stock"
                    />
                </Box>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={addAs}
                    onChange={(_, value) => value && setAddAs(value)}
                    aria-label="Add as"
                    sx={{ mt: 0.5, flexShrink: 0 }}
                >
                    <ToggleButton value="product" aria-label="Add as a paid product" sx={{ px: 1.5 }}>
                        Product
                    </ToggleButton>
                    <ToggleButton value="gift" color="secondary" aria-label="Add as a free gift" sx={{ px: 1.5, gap: 0.5 }}>
                        <CardGiftcard fontSize="small" />
                        Gift
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            {hint && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                    {hint}
                </Typography>
            )}

            {fields.length === 0 ? (
                <Typography
                    variant="body2"
                    color={errors.items ? "error" : "text.secondary"}
                    sx={{ mt: 1.5, py: 2, textAlign: "center", border: 1, borderStyle: "dashed", borderColor: "divider", borderRadius: 1 }}
                >
                    {errors.items?.message || "No products yet — search above to add one."}
                </Typography>
            ) : (
                <Box sx={{ mt: 1 }}>
                    {fields.map((field, index) => {
                        const line = lines[index] ?? (field as unknown as OrderFormLine);
                        return (
                            <OrderLineRow
                                key={field.id}
                                index={index}
                                line={line}
                                product={line.product_id ? productDetailsById.get(line.product_id) : undefined}
                                control={control}
                                errors={errors}
                                onQuantity={onQuantity}
                                onUnitPrice={onUnitPrice}
                                onRemove={(i) => {
                                    setHint("");
                                    onRemove(i);
                                }}
                                removeBlocked={
                                    keepOneLine && fields.length === 1
                                        ? "An order needs at least one item — cancel the order instead."
                                        : undefined
                                }
                                alreadyReserved={line.item_id ? reservedByItemId.get(line.item_id) ?? 0 : 0}
                                checkStock={checkStock}
                                hint={itemHints?.[index]}
                            />
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default ItemsSection;
